import React from 'react';
import { 
  ArrowBigUp, 
  ArrowBigDown, 
  Reply, 
  Image as ImageIcon, 
  Link as LinkIcon, 
  Smile, 
  ChevronDown, 
  ChevronRight, 
  MinusSquare, 
  PlusSquare,
  Send
} from 'lucide-react';
import { MessageBoardComment } from '../types';
import { Button } from './ui/Button';
import { Avatar } from './ui/Avatar';
import { cn, safeDistanceToNow } from '../lib/utils';
import { useCreateComment } from '../hooks/useDiscussions';
import { RichContentViewer } from './RichContentViewer';
import { motion, AnimatePresence } from 'motion/react';

interface CommentNodeProps {
  comment: MessageBoardComment;
  topicId: string;
  depth?: number;
  opAuthorId?: string;
}

export const CommentNode: React.FC<CommentNodeProps> = ({ 
  comment, 
  topicId, 
  depth = 0,
  opAuthorId
}) => {
  const [isReplying, setIsReplying] = React.useState(false);
  const [replyContent, setReplyContent] = React.useState('');
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const [userVote, setUserVote] = React.useState<number>(0);
  
  const baseVotes = React.useMemo(() => {
    // Generate a deterministic initial vote count based on comment id
    let hash = 0;
    for (let i = 0; i < comment.id.length; i++) hash += comment.id.charCodeAt(i);
    return (hash % 7) + 1;
  }, [comment.id]);
  
  const currentVotes = baseVotes + userVote;
  const createComment = useCreateComment();
  const isOp = opAuthorId && comment.authorUserId === opAuthorId;

  const handleVote = (direction: number) => {
    if (userVote === direction) {
      setUserVote(0);
    } else {
      setUserVote(direction);
    }
  };

  const handleReply = async () => {
    if (!replyContent.trim()) return;
    await createComment.mutateAsync({
      topicId,
      parentId: comment.id,
      content: replyContent,
    });
    setReplyContent('');
    setIsReplying(false);
  };

  return (
    <div className={cn(
      "relative group/comment transition-all",
      depth > 0 && "ml-3 sm:ml-6 mt-3 border-l-2 border-brand-primary/20 pl-3 sm:pl-5 space-y-3"
    )}>
      <div className="bg-white dark:bg-slate-900 border-2 border-black/10 dark:border-white/10 p-4 sm:p-5 shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF] rounded-none">
        {/* Comment Header */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <Avatar 
              userId={comment.authorUserId} 
              initials={comment.authorName} 
              size="sm" 
            />
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <span className="font-bold text-sm text-navy-900 dark:text-white truncate">
                {comment.authorName}
              </span>
              {isOp && (
                <span className="px-1.5 py-0.5 text-[9px] font-black uppercase font-mono tracking-widest bg-brand-primary/10 text-brand-primary border border-brand-primary/30">
                  Author
                </span>
              )}
              <span className="text-[10px] text-gray-400 font-mono uppercase tracking-widest">
                {safeDistanceToNow(comment.createdAt)} ago
              </span>
            </div>
          </div>

          <button 
            type="button"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="text-gray-400 hover:text-black dark:hover:text-white transition-colors p-1 flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider shrink-0"
            title={isCollapsed ? "Expand comment" : "Collapse comment"}
          >
            {isCollapsed ? (
              <>
                <PlusSquare size={13} />
                <span className="hidden sm:inline">Expand</span>
              </>
            ) : (
              <>
                <MinusSquare size={13} />
                <span className="hidden sm:inline">Collapse</span>
              </>
            )}
          </button>
        </div>

        {/* Collapsed Preview */}
        {isCollapsed ? (
          <div 
            onClick={() => setIsCollapsed(false)} 
            className="text-xs text-gray-400 italic cursor-pointer hover:text-brand-primary transition-colors py-1"
          >
            Comment thread collapsed • Click to expand
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Comment Content */}
              <div className="text-gray-800 dark:text-gray-200 text-sm leading-relaxed mb-4 pl-0.5">
                <RichContentViewer content={comment.content} />
              </div>

              {/* Action Bar */}
              <div className="flex items-center gap-3 pt-3 border-t border-gray-100 dark:border-slate-800">
                {/* Segmented Up/Down Vote Pill */}
                <div className="inline-flex items-center gap-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 px-2 py-1 text-xs font-mono font-bold">
                  <button 
                    type="button" 
                    onClick={() => handleVote(1)} 
                    className={cn("transition-colors p-0.5", userVote === 1 ? "text-orange-500 font-black" : "hover:text-orange-500 text-gray-400")}
                    title="Upvote"
                  >
                    <ArrowBigUp size={15} className={cn(userVote === 1 && "fill-orange-500")} />
                  </button>
                  <span className={cn(
                    "min-w-[14px] text-center",
                    userVote === 1 && "text-orange-500 font-black", 
                    userVote === -1 && "text-blue-500 font-black",
                    userVote === 0 && "text-gray-500 dark:text-gray-400"
                  )}>
                    {currentVotes}
                  </span>
                  <button 
                    type="button" 
                    onClick={() => handleVote(-1)} 
                    className={cn("transition-colors p-0.5", userVote === -1 ? "text-blue-500 font-black" : "hover:text-blue-500 text-gray-400")}
                    title="Downvote"
                  >
                    <ArrowBigDown size={15} className={cn(userVote === -1 && "fill-blue-500")} />
                  </button>
                </div>

                {/* Reply Button */}
                <button 
                  type="button"
                  onClick={() => setIsReplying(!isReplying)}
                  className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold font-mono uppercase tracking-wider text-gray-600 dark:text-gray-300 hover:text-brand-primary bg-gray-50 dark:bg-slate-800 hover:bg-brand-primary/10 transition-colors border border-gray-200 dark:border-slate-700"
                >
                  <Reply size={13} />
                  Reply
                </button>
              </div>
            </motion.div>
          </AnimatePresence>
        )}
      </div>

      {/* Nested Replies */}
      <AnimatePresence>
        {!isCollapsed && comment.replies && comment.replies.length > 0 && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-3"
          >
            {comment.replies.map((reply) => (
              <CommentNode 
                key={reply.id} 
                comment={reply} 
                topicId={topicId} 
                depth={depth + 1}
                opAuthorId={opAuthorId}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Inline Reply Composer (Appears below the comment and any existing replies) */}
      <AnimatePresence>
        {!isCollapsed && isReplying && (
          <motion.div 
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className={cn(
              "space-y-3 bg-gray-50 dark:bg-slate-950 p-4 border-l-4 border-brand-primary border-t border-r border-b border-gray-200 dark:border-slate-800 shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF]",
              (comment.replies && comment.replies.length > 0) ? "ml-3 sm:ml-6 mt-3" : "mt-3"
            )}
          >
            <div className="flex items-center justify-between text-xs font-mono text-gray-500">
              <span className="font-bold">Replying to {comment.authorName}</span>
              <span className="text-[10px] text-gray-400 uppercase tracking-widest">Markdown supported</span>
            </div>
            <textarea
              autoFocus
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  handleReply();
                }
              }}
              placeholder={`Write your reply to ${comment.authorName}...`}
              className="w-full bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 p-3 text-sm focus:border-brand-primary outline-none transition-all min-h-[90px] text-navy-900 dark:text-white"
            />
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-gray-400 font-mono hidden sm:inline">
                Press ⌘+Enter to submit
              </span>
              <div className="flex items-center gap-2 ml-auto">
                <Button variant="ghost" size="sm" onClick={() => setIsReplying(false)}>
                  Cancel
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleReply}
                  isLoading={createComment.isPending}
                  disabled={!replyContent.trim()}
                  className="gap-1.5"
                >
                  <Send size={13} />
                  Post Reply
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
